import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { setStoredLanguage } from '../utils/languageService';

/**
 * Display metadata for known language codes.
 * Falls back to uppercase code for unknown languages.
 */
const LANG_META = {
  en: { label: 'English' },
  fr: { label: 'Français' },
};

const getLangMeta = (code) =>
  LANG_META[code] ?? { label: code.toUpperCase() };

/**
 * Reusable language switcher.
 * - Button shows short code only: EN / FR
 * - Dropdown shows: EN | English / FR | Français
 * - Languages derived dynamically from i18n configuration
 */
export default function LanguageSwitcher({ theme = 'auto', showLabel = false, className = '' }) {
  const { i18n, t } = useTranslation('common');
  const { isDark } = useTheme();
  const currentLang = (i18n.language || '').split('-')[0] || 'en';
  const useDark = theme === 'dark' ? true : theme === 'light' ? false : isDark;
  const [isOpen, setIsOpen] = useState(false);
  // pos.left xor pos.right is set depending on which side has more room
  const [pos, setPos] = useState({ top: 0, right: 0, left: null });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const availableLanguages = useMemo(() => {
    const fromSupported = Array.isArray(i18n.options?.supportedLngs)
      ? i18n.options.supportedLngs
      : [];
    const fromResources = i18n.options?.resources
      ? Object.keys(i18n.options.resources)
      : [];
    const merged = [...fromSupported, ...fromResources]
      .filter(Boolean)
      .map((lng) => String(lng).split('-')[0].toLowerCase())
      .filter((lng) => lng && lng !== 'cimode')
      .filter((lng, idx, arr) => arr.indexOf(lng) === idx);
    return merged;
  }, [i18n.options?.supportedLngs, i18n.options?.resources]);

  const changeLanguage = (lng) => {
    if (currentLang !== lng) {
      i18n.changeLanguage(lng);
      setStoredLanguage(lng);
    }
    setIsOpen(false);
  };

  const openMenu = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const DROPDOWN_MIN_W = 180;
      const EDGE_GAP = 8;
      const spaceOnRight = window.innerWidth - rect.right;
      // If right-anchoring the dropdown would push its left edge off-screen, use left-anchoring instead
      const wouldClipLeft = rect.right - DROPDOWN_MIN_W < EDGE_GAP;
      if (wouldClipLeft) {
        setPos({
          top: rect.bottom + 6,
          left: Math.max(EDGE_GAP, rect.left),
          right: null,
        });
      } else {
        setPos({
          top: rect.bottom + 6,
          left: null,
          right: Math.max(EDGE_GAP, spaceOnRight),
        });
      }
    }
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return;
    const onClickOutside = (event) => {
      const insideTrigger = triggerRef.current?.contains(event.target);
      const insideMenu = menuRef.current?.contains(event.target);
      if (!insideTrigger && !insideMenu) setIsOpen(false);
    };
    const onEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [isOpen]);

  if (availableLanguages.length === 0) return null;

  return (
    <div className={`flex items-center gap-2 flex-shrink-0 ${className}`}>
      {showLabel ? (
        <span className={`text-sm ${useDark ? 'text-zinc-300' : 'text-zinc-600'}`}>
          {t('common.language')}
        </span>
      ) : null}
      <button
        ref={triggerRef}
        onClick={() => (isOpen ? setIsOpen(false) : openMenu())}
        className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors ${
          useDark
            ? 'border-white/20 bg-white/5 hover:bg-white/10'
            : 'border-black/12 bg-black/[0.04] hover:bg-black/[0.07]'
        }`}
        aria-label="Language switcher"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={useDark ? 'text-[#f0d9a6]' : 'text-[#7d6633]'}>
          {currentLang.toUpperCase()}
        </span>
        <svg
          className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''} ${useDark ? 'text-white/50' : 'text-black/40'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
            <div
              ref={menuRef}
              role="listbox"
              aria-label="Language options"
              className={`fixed z-[9999] min-w-[170px] rounded-md border py-1 shadow-lg ${
                useDark
                  ? 'border-white/15 bg-zinc-900/95'
                  : 'border-black/10 bg-white/95'
              }`}
              style={{
                top: pos.top,
                ...(pos.left !== null ? { left: pos.left } : { right: pos.right }),
                maxWidth: 'calc(100vw - 16px)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
              }}
            >
              {availableLanguages.map((lng) => {
                const active = currentLang === lng;
                const { label } = getLangMeta(lng);
                return (
                  <button
                    key={lng}
                    onClick={() => changeLanguage(lng)}
                    className={`w-full text-left flex items-center px-3 py-2 text-sm transition-colors ${
                      active
                        ? useDark
                          ? 'text-[#f0d9a6] bg-[#7d6633]/30'
                          : 'text-[#7d6633] bg-[#7d6633]/10'
                        : useDark
                          ? 'text-white/70 hover:text-white hover:bg-white/10'
                          : 'text-black/60 hover:text-black/85 hover:bg-black/[0.04]'
                    }`}
                    aria-pressed={active}
                    aria-label={`Switch to ${label}`}
                  >
                    <span className="font-semibold uppercase tracking-wide w-7 flex-shrink-0">
                      {lng.toUpperCase()}
                    </span>
                    <span className={`mx-2 select-none ${useDark ? 'text-white/25' : 'text-black/25'}`}>|</span>
                    <span className="flex-1">{label}</span>
                    {active && (
                      <svg
                        className="w-3.5 h-3.5 flex-shrink-0 ml-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </>,
          document.body
        )}
    </div>
  );
}
